import { useCallback, useEffect, useRef, useState } from "react";
import {
  Loader2, Camera, Save, Trash2, Check, X, StickyNote, ImageOff,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const SLOTS = Array.from({ length: 13 }, (_, i) => `${String(i + 8).padStart(2, "0")}:00`);
const slotLabel = (s) => {
  const h = parseInt(s, 10);
  if (h === 12) return "12 PM";
  return h < 12 ? `${h} AM` : `${h - 12} PM`;
};

const inputCls =
  "w-full rounded-xl border border-emerald-950/15 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-limed/50";

function PhotoThumb({ photo, onDelete }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let objectUrl;
    let cancelled = false;
    api
      .get(`/admin/photos/${photo.id}`, { responseType: "blob" })
      .then((res) => {
        if (!cancelled) {
          objectUrl = URL.createObjectURL(res.data);
          setUrl(objectUrl);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [photo.id]);

  return (
    <div className="relative group rounded-2xl overflow-hidden border border-emerald-950/10 bg-sage/40 aspect-square">
      {url ? (
        <img src={url} alt={photo.original_filename} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-emerald-900/30">
          <Loader2 size={18} className="animate-spin" />
        </div>
      )}
      <button
        data-testid={`photo-delete-${photo.id}`}
        onClick={() => onDelete(photo.id)}
        title="Remove photo"
        className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 text-red-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
      >
        <Trash2 size={13} />
      </button>
      <p className="absolute bottom-0 inset-x-0 bg-forest/60 text-bone text-[10px] px-2 py-1 truncate">
        {photo.uploaded_by}
      </p>
    </div>
  );
}

export default function BookingDetail({ bookingId, onClose, onChanged }) {
  const [booking, setBooking] = useState(null);
  const [services, setServices] = useState([]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!bookingId) return;
    try {
      const [{ data: b }, { data: s }] = await Promise.all([
        api.get(`/admin/bookings/${bookingId}`),
        api.get("/services"),
      ]);
      setBooking(b);
      setServices(s);
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't load this booking."));
      onClose();
    }
  }, [bookingId, onClose]);

  useEffect(() => {
    setBooking(null);
    load();
  }, [load]);

  const set = (k) => (e) => setBooking({ ...booking, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/bookings/${bookingId}`, {
        name: booking.name,
        email: booking.email,
        phone: booking.phone,
        address: booking.address,
        service: booking.service,
        date: booking.date,
        time_slot: booking.time_slot,
        notes: booking.notes || "",
        status: booking.status,
      });
      toast.success("Booking updated.");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    try {
      await api.post(`/admin/bookings/${bookingId}/notes`, { text: note.trim() });
      setNote("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/admin/bookings/${bookingId}/photos`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo added.");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Photo upload failed."));
    } finally {
      setUploading(false);
    }
  };

  const deletePhoto = async (photoId) => {
    try {
      await api.delete(`/admin/photos/${photoId}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete the booking for ${booking.name}? This frees up the slot and can't be undone.`)) return;
    try {
      await api.delete(`/admin/bookings/${bookingId}`);
      toast.success("Booking deleted — the slot is open again.");
      onChanged();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Dialog open={!!bookingId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="sm:max-w-2xl bg-bone max-h-[90vh] overflow-y-auto"
        data-testid="booking-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-emerald-950">Booking details</DialogTitle>
          <DialogDescription className="text-emerald-900/60">
            View, edit, add notes, and attach photos of the finished work.
          </DialogDescription>
        </DialogHeader>

        {!booking ? (
          <div className="flex justify-center py-16 text-pine">
            <Loader2 size={26} className="animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-emerald-900/50 mb-1.5 block">Service</label>
                <select data-testid="detail-service" value={booking.service} onChange={set("service")} className={`${inputCls} bg-white`}>
                  {services.map((s) => (
                    <option key={s.id} value={s.name}>{s.name} — {s.price}</option>
                  ))}
                  {!services.some((s) => s.name === booking.service) && (
                    <option value={booking.service}>{booking.service}</option>
                  )}
                </select>
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-emerald-900/50 mb-1.5 block">Status</label>
                <select data-testid="detail-status" value={booking.status} onChange={set("status")} className={`${inputCls} bg-white`}>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="declined">Declined</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-emerald-900/50 mb-1.5 block">Date</label>
                <input data-testid="detail-date" type="date" value={booking.date} onChange={set("date")} className={inputCls} />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-emerald-900/50 mb-1.5 block">Time</label>
                <select data-testid="detail-time" value={booking.time_slot} onChange={set("time_slot")} className={`${inputCls} bg-white`}>
                  {SLOTS.map((s) => (
                    <option key={s} value={s}>{slotLabel(s)}</option>
                  ))}
                </select>
              </div>
              <input data-testid="detail-name" value={booking.name} onChange={set("name")} placeholder="Customer name" className={inputCls} />
              <input data-testid="detail-phone" value={booking.phone} onChange={set("phone")} placeholder="Phone" className={inputCls} />
              <input data-testid="detail-email" type="email" value={booking.email} onChange={set("email")} placeholder="Email" className={inputCls} />
              <input data-testid="detail-address" value={booking.address} onChange={set("address")} placeholder="Job address" className={inputCls} />
              <textarea
                data-testid="detail-notes"
                value={booking.notes || ""}
                onChange={set("notes")}
                placeholder="Customer's notes for the crew"
                rows={2}
                className={`${inputCls} sm:col-span-2 resize-none`}
              />
            </div>

            <div className="flex flex-wrap gap-2.5">
              <button
                data-testid="detail-save-btn"
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-2 bg-pine text-bone text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-limed hover:text-pine-dark transition-colors disabled:opacity-60"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save Changes
              </button>
              <button
                data-testid="detail-confirm-btn"
                onClick={() => setBooking({ ...booking, status: "confirmed" })}
                className="inline-flex items-center gap-1.5 text-xs font-semibold bg-limed-light text-pine px-4 py-2.5 rounded-full hover:bg-limed hover:text-pine-dark transition-colors"
              >
                <Check size={13} /> Mark Confirmed
              </button>
              <button
                data-testid="detail-decline-btn"
                onClick={() => setBooking({ ...booking, status: "declined" })}
                className="inline-flex items-center gap-1.5 text-xs font-semibold border border-emerald-950/15 text-emerald-900/60 px-4 py-2.5 rounded-full hover:border-red-300 hover:text-red-500 transition-colors"
              >
                <X size={13} /> Mark Declined
              </button>
              <button
                data-testid="detail-delete-btn"
                onClick={remove}
                className="inline-flex items-center gap-1.5 text-xs font-semibold border border-red-200 text-red-500 px-4 py-2.5 rounded-full hover:bg-red-50 transition-colors ml-auto"
              >
                <Trash2 size={13} /> Delete
              </button>
            </div>

            <div className="border-t border-emerald-950/10 pt-5">
              <p className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-emerald-900/50 mb-3">
                <StickyNote size={13} /> Crew notes
              </p>
              <div className="space-y-2 mb-3">
                {(booking.crew_notes || []).length === 0 && (
                  <p className="text-sm text-emerald-900/40">No notes yet.</p>
                )}
                {(booking.crew_notes || []).map((n) => (
                  <div key={n.id} data-testid={`crew-note-${n.id}`} className="bg-white rounded-xl border border-emerald-950/10 px-4 py-2.5">
                    <p className="text-sm text-emerald-950">{n.text}</p>
                    <p className="text-[11px] font-mono text-emerald-900/40 mt-1">
                      {n.author} · {new Date(n.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    </p>
                  </div>
                ))}
              </div>
              <form onSubmit={addNote} className="flex gap-2">
                <input
                  data-testid="note-input"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add a note — e.g. gate code, gate was locked, bring extra bags"
                  className={`${inputCls} flex-1`}
                />
                <button
                  data-testid="note-add-btn"
                  type="submit"
                  className="shrink-0 inline-flex items-center gap-1.5 bg-sage text-pine text-xs font-semibold px-4 py-2.5 rounded-full hover:bg-limed-light transition-colors"
                >
                  Add Note
                </button>
              </form>
            </div>

            <div className="border-t border-emerald-950/10 pt-5">
              <div className="flex items-center justify-between mb-3">
                <p className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-emerald-900/50">
                  <Camera size={13} /> Work photos ({(booking.photos || []).length})
                </p>
                <button
                  data-testid="photo-upload-btn"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex items-center gap-1.5 bg-pine text-bone text-xs font-semibold px-4 py-2 rounded-full hover:bg-limed hover:text-pine-dark transition-colors disabled:opacity-60"
                >
                  {uploading ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />}
                  {uploading ? "Uploading..." : "Add Photo"}
                </button>
                <input
                  ref={fileRef}
                  data-testid="photo-file-input"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={uploadPhoto}
                  className="hidden"
                />
              </div>
              {(booking.photos || []).length === 0 ? (
                <p className="text-sm text-emerald-900/40 flex items-center gap-2">
                  <ImageOff size={15} /> No photos yet — snap one when the job's done.
                </p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {booking.photos.map((p) => (
                    <PhotoThumb key={p.id} photo={p} onDelete={deletePhoto} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
